import React, { FC } from 'react';
import { stringFormat, T } from 'src/shared/helpers';
import { AdvancedSelect } from '../../widgets/select';
import {
    PackageDiscountBadge,
    PackageOptionLabel,
    PackageOptionPrice,
    PackageOriginalPrice,
    TierSelectionSection
} from './serviceCreditsComponents';

/** A credit-package tier, straight from `addon(addonKey, currencyCode).rates`. */
export interface CreditPackRate {
    rateID: number;
    usageFrom: number;
    feeValue: number;
}

/** A built, display-ready credit-package option (dropdown value + label + per-tier discount math). */
export interface CreditPackOption {
    value: number;
    label: string;
    usageFrom: number;
    price: string;
    originalPrice: string | null;
    discountPerc: number;
}

/**
 * Builds the display-ready credit-package options from raw rates. From the second package onwards a discount is
 * computed against the first package's per-credit price, exposing the full (non-discounted) price and discount %.
 * Shared so every credit picker (the dropdown here, the AI Studio no-credits modal) shows identical pricing.
 */
export const buildCreditPackOptions = (rates: CreditPackRate[], formatPrice: (value: number) => string): CreditPackOption[] => {
    const baseUnitPrice = rates.length > 0 && rates[0].usageFrom > 0 ? rates[0].feeValue / rates[0].usageFrom : 0;
    return rates.map((rate, index) => {
        const fullPrice = baseUnitPrice * rate.usageFrom;
        const discountPerc = index > 0 && fullPrice > rate.feeValue ? Math.round((1 - rate.feeValue / fullPrice) * 100) : 0;
        return {
            value: rate.rateID,
            label: `${rate.usageFrom} ${T._('credits', 'Admin')} - ${formatPrice(rate.feeValue)}`,
            usageFrom: rate.usageFrom,
            price: formatPrice(rate.feeValue),
            originalPrice: discountPerc > 0 ? formatPrice(fullPrice) : null,
            discountPerc
        };
    });
};

/** Renders a credit-package option row (crossed-out original price + discount badge). Shared across pickers. */
export const formatCreditPackOption = (option: CreditPackOption) => (
    <PackageOptionLabel>
        <span>{stringFormat(T._('{0} credits', 'Admin'), String(option.usageFrom))}</span>
        <PackageOptionPrice>
            {option.originalPrice && <PackageOriginalPrice>{option.originalPrice}</PackageOriginalPrice>}
            <span>{option.price}</span>
            {option.discountPerc > 0 && <PackageDiscountBadge>{stringFormat(T._('-{0}%', 'Admin'), String(option.discountPerc))}</PackageDiscountBadge>}
        </PackageOptionPrice>
    </PackageOptionLabel>
);

interface CreditPackSelectorProps {
    /** Credit-package tiers for the service. */
    rates: CreditPackRate[];
    /** Formats a raw feeValue into the display currency string (the caller owns exchange-rate conversion). */
    formatPrice: (value: number) => string;
    selectedTier: number | null;
    onSelectTier: (tier: number | null) => void;
    /** Disables the dropdown (e.g. while a purchase is in flight). */
    disabled?: boolean;
    /** Overrides the field label. */
    label?: string;
}

/**
 * Shared credit-package tier dropdown: renders the selectable packages with per-tier discount math (the full,
 * non-discounted price crossed out alongside the discount %). Extracted verbatim from the My Account credits
 * page so the discovery-mode buy-credits dialog renders the exact same picker.
 *
 * Presentational only — the caller owns data fetching, currency conversion (`formatPrice`), the auto-refill
 * toggle and the buy mutation. (Auto-refill lives with each caller: the My Account page persists it via
 * toggleAutoRefill, while discovery just passes it to the one-off buy.)
 */
export const CreditPackSelector: FC<CreditPackSelectorProps> = ({
    rates,
    formatPrice,
    selectedTier,
    onSelectTier,
    disabled = false,
    label
}) => {
    const packageOptions = buildCreditPackOptions(rates, formatPrice);
    const formatPackageOption = formatCreditPackOption;

    return (
        <TierSelectionSection>
            <label>{label ?? T._('Select Credit Package', 'Admin')}</label>
            <AdvancedSelect
                isSearchable={false}
                menuPosition={'fixed'}
                menuPlacement={'auto'}
                isDisabled={disabled}
                options={packageOptions}
                value={packageOptions.find((option) => option.value === selectedTier) || null}
                formatOptionLabel={formatPackageOption}
                onChange={(val) => {
                    onSelectTier(val?.value ?? null);
                }}
                placeholder={T._('Select a package...', 'Admin')}
            />
        </TierSelectionSection>
    );
};

export default CreditPackSelector;
